import "./ViewOrEdit.css"

import { useState, useMemo } from "react"

import { RecordContext } from "web/contexts"

import { Form, isModalEditable, openEditModal } from "web/components"
import {
  getFormValue,
  getGlobalValue,
  getSectionsToShowWithOtherToggle,
  fieldIsVisible,
  formValuesAreDifferent,
} from "web/components/Form/helpers"

import { useUnits } from "web/hooks/data"
import { useLockedFieldEdit } from "web/hooks/modals"

import { isObject, plainObjectFromValue } from "web/services/helpers"

import Field from "./Field"
import SectionBadge from "./SectionBadge"
import MissingFieldsMessage from "./MissingFieldsMessage"
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid"

export default function ViewOrEdit({
  missingStageFields,
  supportsMissingStageFields = false,
  data,
  fields,
  fieldFilter = "",
  sections = [],
  showAll = false,
  readOnly = false,
  onFieldChanged,
}) {
  // Warm the units cache so unit inputs don't suspend on first edit
  useUnits()

  const dataValues = isObject(data.values) ? data.values : {}
  const formValue = getFormValue(dataValues, fields)
  const globalValue = getGlobalValue(dataValues, fields)
  const dataForConditions = plainObjectFromValue(globalValue)
  const lockedFieldEditModal = useLockedFieldEdit()

  function handleFieldChanged(key, value) {
    if (onFieldChanged && formValuesAreDifferent(formValue[key], value)) {
      onFieldChanged({ [key]: value })
    }
  }

  function openLockedFieldEditModal(fieldKey, field) {
    return lockedFieldEditModal(fieldKey, field, data, globalValue)
  }

  function wrappedIsModalEditable(fieldKey) {
    return isModalEditable(fieldForKey(fieldKey), data)
  }

  function wrappedOpenEditModal(fieldKey) {
    return openEditModal(fieldForKey(fieldKey), data, "details")
  }

  function fieldForKey(key) {
    return fields.find((f) => f.key === key)
  }

  const matchesQuickFilter = ({ label }) =>
    fieldFilter === "" ||
    label.toString().toLowerCase().includes(fieldFilter.toLowerCase())

  const fieldHasValue = (field) => field.key in dataValues

  const isStageField = (field) =>
    field.isOptionalRequired || field.isMandatoryRequired

  const shownBasedOnValueOrStageStatus = (field) =>
    showAll || fieldHasValue(field) || isStageField(field)

  const isVisible = (field) => fieldIsVisible(field, dataForConditions)

  const fieldsToShow = useMemo(
    () =>
      fields
        .filter(matchesQuickFilter)
        .filter(shownBasedOnValueOrStageStatus)
        .filter(isVisible),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showAll, fields, fieldFilter, data]
  )

  const { configuredSections, otherSection, showOtherFieldsToggle } = useMemo(
    () =>
      getSectionsToShowWithOtherToggle({
        sections,
        fields: fieldsToShow,
      }),
    [sections, fieldsToShow]
  )

  const hasConfiguredSections = configuredSections.length !== 0

  const missingStageFieldsOrDefault = missingStageFields || []
  const missingStageFieldIds = missingStageFieldsOrDefault
    .filter((stageField) => stageField.mandatory)
    .map((stageField) => stageField.field.id)

  const isTopicData = !!data.topic_id
  const showMissingStageFields = supportsMissingStageFields && !isTopicData

  return (
    <RecordContext.Provider value={data}>
      {showMissingStageFields && (
        <MissingFieldsMessage
          resourceType={data.resource_type}
          id={data.id}
          missingStageFields={missingStageFieldsOrDefault}
        />
      )}
      <Form
        value={formValue}
        globalValue={globalValue}
        onFieldChanged={handleFieldChanged}
        fields={fields}
        render={() => {
          return (
            <div className="grid">
              {configuredSections.map((section) => (
                <Section
                  key={section.id}
                  section={section}
                  data={data}
                  readOnly={readOnly}
                  showAll={showAll}
                  showSectionHeader={hasConfiguredSections}
                  missingStageFieldIds={missingStageFieldIds}
                  openLockedFieldEditModal={openLockedFieldEditModal}
                  isModalEditable={wrappedIsModalEditable}
                  openEditModal={wrappedOpenEditModal}
                />
              ))}
              {showOtherFieldsToggle && (
                <OtherFieldsSection
                  section={otherSection}
                  data={data}
                  readOnly={readOnly}
                  showAll={showAll}
                  missingStageFieldIds={missingStageFieldIds}
                  openLockedFieldEditModal={openLockedFieldEditModal}
                  isModalEditable={wrappedIsModalEditable}
                  openEditModal={wrappedOpenEditModal}
                />
              )}
            </div>
          )
        }}
      />
    </RecordContext.Provider>
  )
}

function Section({
  section,
  data,
  readOnly,
  showAll,
  showSectionHeader,
  missingStageFieldIds,
  openLockedFieldEditModal,
  isModalEditable,
  openEditModal,
}) {
  function numberOfMissingFieldsInSection(section) {
    return section.fields.filter((f) => f.id in missingStageFieldIds).length
  }

  const lockedFieldKeys = Object.keys(data.locked_fields || {})

  return (
    <section className="py-6 first:pt-0">
      {showSectionHeader && (
        <header className="flex items-center border-b border-neutral-9 pb-2 text-base font-bold text-neutral-2">
          {section.label}
          <SectionBadge>{numberOfMissingFieldsInSection(section)}</SectionBadge>
        </header>
      )}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(340px,1fr))] gap-x-10">
        {section.fields.map((field) => {
          const valueLocked = lockedFieldKeys.includes(field.key)

          return (
            <div
              key={field.id}
              className="border-b border-neutral-9 py-1.5 empty:hidden"
            >
              <Field
                data={data.values}
                fieldKey={field.key}
                readOnly={readOnly}
                showAll={showAll}
                mode={showAll && !readOnly ? "edit" : undefined}
                valueLocked={valueLocked}
                openLockedFieldEditModal={openLockedFieldEditModal}
                isModalEditable={isModalEditable}
                openEditModal={openEditModal}
              />
            </div>
          )
        })}
      </div>
    </section>
  )
}

function OtherFieldsSection({
  section,
  data,
  readOnly,
  showAll,
  missingStageFieldIds,
  openLockedFieldEditModal,
  isModalEditable,
  openEditModal,
}) {
  function numberOfMissingFieldsInSection(section) {
    return section.fields.filter((f) => f.id in missingStageFieldIds).length
  }

  const lockedFieldKeys = Object.keys(data.locked_fields || {})

  const fieldCount = section.fields.length
  const [expanded, setExpanded] = useState(false)

  return (
    <section className="py-6 first:pt-0">
      <header
        className="flex cursor-pointer items-center border-b border-neutral-9 pb-2 text-base font-bold text-neutral-2 hover:text-feature-6"
        onClick={() => {
          setExpanded(!expanded)
        }}
      >
        <span className="-ml-1 mr-1 h-5 w-5">
          {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
        </span>
        {`Other Fields (${fieldCount})`}
        <SectionBadge>{numberOfMissingFieldsInSection(section)}</SectionBadge>
      </header>
      {expanded && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(340px,1fr))] gap-x-10">
          {section.fields.map((field) => {
            const valueLocked = lockedFieldKeys.includes(field.key)

            return (
              <div
                key={field.id}
                className="border-b border-neutral-9 py-1.5 empty:hidden"
              >
                <Field
                  data={data.values}
                  fieldKey={field.key}
                  readOnly={readOnly}
                  showAll={showAll}
                  mode={showAll && !readOnly ? "edit" : undefined}
                  valueLocked={valueLocked}
                  openLockedFieldEditModal={openLockedFieldEditModal}
                  isModalEditable={isModalEditable}
                  openEditModal={openEditModal}
                />
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
